import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useState } from "react";

interface ChamadoRatingProps {
  onSubmitRating: (rating: number, comment: string) => Promise<void>;
}

export default function ChamadoRating({ onSubmitRating }: ChamadoRatingProps) {
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (selectedRating !== null) {
      try {
        setIsSubmitting(true);
        await onSubmitRating(selectedRating, comment);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 border-t pt-4">
        <h3 className="font-semibold text-n-green">
          O seu chamado foi finalizado!
        </h3>
      </div>

      <Card className="max-w-full mx-auto">
        <CardHeader>
          <CardTitle className="text-start text-n-text-neutral font-normal">
            Qual o seu grau de satisfação com o nosso atendimento?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-10">
          <div className="space-y-2 flex flex-col items-center">
            {/* Desktop: Labels horizontais */}
            <div className="hidden sm:flex justify-between items-center text-xs font-semibold text-n-text-neutral gap-[370px]">
              <span>Insatisfeito</span>
              <span>Muito Satisfeito</span>
            </div>

            {/* Mobile: Labels verticais acima do grid */}
            <div className="sm:hidden flex justify-between text-xs font-semibold text-n-text-neutral px-1">
              <span>Insatisfeito</span>
              <span>Muito Satisfeito</span>
            </div>

            {/* Botões: Grid 6 colunas no mobile, flex no desktop */}
            <div className="grid grid-cols-6 sm:flex sm:flex-wrap sm:justify-center gap-2">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                <button
                  key={rating}
                  onClick={() => setSelectedRating(rating)}
                  disabled={isSubmitting}
                  className={`w-10 h-10 rounded-lg border-2 font-semibold transition-all text-sm ${
                    selectedRating === rating
                      ? rating === 2
                        ? "bg-green-500 border-green-500 text-white"
                        : "bg-n-green border-n-green text-white"
                      : "border-gray-300 hover:border-n-green hover:bg-n-green/10"
                  } ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {rating}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">
              Se desejar, adicione um comentário:
            </label>
            <Textarea
              placeholder="Escreva a sua mensagem..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="flex justify-start">
            <Button
              onClick={handleSubmit}
              disabled={selectedRating === null || isSubmitting}
              className="px-8 h-10"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
